import {
  Component,
  EventEmitter,
  Input,
  Output,
  Inject,
  OnChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  Offer,
  paymentFrequencies,
  paymentTerms,
  offerStatuses,
} from 'app/services/offers/offer.model';
import { ConfirmationDialogComponent } from 'ng2-app/modules/confirmation-dialog/confirmation-dialog.component';
import { PublishOfferDialogComponent } from 'ng2-app/offer/publish-offer-dialog/publish-offer-dialog.component';
import * as _ from 'lodash';
import {
  FormlyDialogComponent,
  FormlyDialogData,
} from 'ng2-app/components/formly-dialog/formly-dialog.component';
import { DeleteOffer } from 'app/store/offers/offer.actions';
import { UntypedFormGroup } from '@angular/forms';
import { OfferFormFields } from '../offer.formly-fields';
import { OffersService } from 'app/services/offers/offers.service';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { of, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Store, Select } from '@ngxs/store';
import { ClipboardService } from 'ngx-clipboard';
import { RollbarService } from 'app/rollbar';
import Rollbar from 'rollbar';

@Component({
  selector: 'app-offer',
  templateUrl: './offer.component.html',
  styleUrls: ['./offer.component.scss'],
})
export class OfferComponent implements OnChanges {
  @Input() offer: Offer;
  @Input() offerNumber: number;
  @Input() acceptedOfferId: number | null = null;
  @Input() dealStage: string;
  @Input() canEdit: boolean;
  @Input() canEditInactiveOffers = false;
  @Input() canDelete: boolean;
  @Input() readOnly: boolean = false;
  @Input() hasContractRequest: boolean = false;
  @Input() cardExpandable: boolean = true;
  @Input() allowDuplicate: boolean = true;
  @Input() renderType: 'fullCard' | 'actionsDropdown' = 'fullCard';

  @Output() publish = new EventEmitter<Offer>();
  @Output() acceptedToggle = new EventEmitter<{
    id: number;
    checked: boolean;
  }>();
  @Output() expandToggle = new EventEmitter<number>();
  @Output() editOffer = new EventEmitter<{ offer: Offer }>();
  @Output() createOffer = new EventEmitter<{ offer: Offer }>();
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Output() removeFromPortal = new EventEmitter<Offer>();
  @Output() deleteOffer = new EventEmitter<number>();
  borrowerHasChosen: boolean;
  canDeleteOffer: boolean;
  canEditOffer: boolean;
  disableOffer: boolean;
  isPublished: boolean;
  canBePublished: boolean;
  hasPublishPermission: boolean;
  canBeRemovedFromPortal: boolean;
  canCreateContractRequest: boolean;
  offerExpiredFormatted: Date;
  offerFormFields = new OfferFormFields();
  offerRecievedFormatted: Date;
  paymentFrequencies = paymentFrequencies;
  paymentTerms = paymentTerms;
  offerStatus: { value: string, display: string } = offerStatuses['draft'];
  isOfferAccepted: boolean;

  constructor(
    @Inject(RollbarService) private rollbar: Rollbar,
    private dialog: MatDialog,
    private clipboard: ClipboardService,
    private offersService: OffersService,
  ) { }

  ngOnChanges() {
    this.offerRecievedFormatted = new Date(this.offer.received);
    if (this.offer.expiration) {
      this.offerExpiredFormatted = new Date(this.offer.expiration);
    }
    this.setParams();
  }

  _borrowerHasChosen() {
    return !!(this.offer.borrowerAccepted || this.offer.borrowerDeclined);
  }

  _isPublished() {
    return !!this.offer.publishedToBorrower;
  }

  _disableOffer() {
    return !!this.acceptedOfferId && this.acceptedOfferId !== this.offer.id;
  }

  _offerStatus() {
    if (!!this.acceptedOfferId && this.acceptedOfferId === this.offer.id) {
      return this.hasContractRequest ? 'contractRequested' : 'accepted';
    }
    if (this._isPublished()) {
      return 'published';
    }
    return 'draft';
  }

  setParams() {
    this.borrowerHasChosen = this._borrowerHasChosen();
    this.disableOffer = this._disableOffer();
    this.isPublished = this._isPublished();
    this.lenderUser$.pipe(take(1)).subscribe((user) => {
      this.hasPublishPermission = user.permissions.includes('canPublishOffersToPortal');
    });

    this.canBePublished = (
      !this.borrowerHasChosen
      && !this.isPublished
      && !this.readOnly
      && this.hasPublishPermission
      && this.offer.loanProductType !== 'equipment'
    );

    this.canBeRemovedFromPortal =
      !this.borrowerHasChosen &&
      !this.offer.accepted &&
      this.isPublished &&
      !this.readOnly &&
      this.dealStage !== 'funded';

    this.offerStatus = offerStatuses[this._offerStatus()];

    const invalidStagesForCRCreate = ['application','funded','inactive'];
    const invalidStatusesForCRCreate = ['awaitingResponse','declined'];

    this.canCreateContractRequest = !((invalidStagesForCRCreate.some(stage => stage === this.dealStage)) ||
      (invalidStatusesForCRCreate.some(status => status === this.offerStatus.value))
    );

    this.lenderUser$.pipe(
      take(1)).subscribe(
        (user) => {
          const isFundingDesk = Boolean(
            user?.roles?.find((role) => [
              'fundingDesk',
              'Submission Admin - Funding Desk',
              'Processor - Funding Desk',
              'Offers Admin - Funding Desk'
            ].includes(role))
          );

          this.canEditOffer =
            ((this.canEdit &&
              !this.isPublished &&
              !this.disableOffer &&
              !this.offer.offerLink &&
              this.dealStage !== 'inactive' &&
              this.dealStage !== 'funded') ||
              this.canEditInactiveOffers ||
              user?.permissions?.includes('editOffers')
            ) &&
            !this.offer.apiLogId &&
            (!this.offer.isCreatedByFundingDesk || isFundingDesk);

          this.canDeleteOffer =
            this.canDelete &&
            !this.offer.accepted &&
            !this.borrowerHasChosen &&
            !this.offer.apiLogId &&
            this.dealStage !== 'inactive' &&
            this.dealStage !== 'funded' &&
            (!this.offer.isCreatedByFundingDesk || isFundingDesk);
    });


    this.isOfferAccepted = this.offer.accepted !== null;
  }

  sendOfferToBorrowerPortal() {
    this.publish.emit(this.offer);
  }

  confirmDelete(): void {
    const confirmationPromptText = this.isPublished ?
      'Are you sure you want to delete this offer? This will also remove the offer from borrower portal.' :
      'Are you sure you want to delete this offer?';
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        showHeader: true,
        headerText: 'Delete Offer',
        confirmationPromptText: confirmationPromptText,
        showCancelButton: true,
        confirmButtonText: 'Delete',
      },
    });
    dialogRef.componentInstance.confirm.pipe(take(1)).subscribe(() => {
      this.deleteOffer.emit(this.offer.id);
      dialogRef.close();
    });
  }

  handleDuplicate() {
    this.setCostType();
    const dialogData = this.formlyDialogData();
    _.extend(dialogData.formlyConfig.model, {
      id: null,
      accepted: null,
      name: null,
      offerLink: null,
      url: null,
      received: new Date(),
    });
    dialogData.title = 'Create Duplicate Offer';
    delete dialogData.formlyConfig.model.expiration;
    dialogData.ignoreDirty = true;

    const dialogRef = this.dialog.open(FormlyDialogComponent, {
      data: dialogData,
      width: '60rem',
      panelClass: 'offer-form-dialog',
    });
    dialogRef.componentInstance.saveData.subscribe((data) => {
      const offer = { ...data.formlyConfig.form.value };
      if (offer.costType) {
        offer[offer.costType] = offer.cost;
      }
      this.createOffer.emit({ ...offer });
      dialogRef.close();
    });
  }

  handleEdit() {
    this.setCostType();
    this.offer.received = new Date(this.offer.received);
    this.offer.expiration = this.offer.expiration
      ? new Date(this.offer.expiration)
      : null;
    const dialogRef = this.dialog.open(FormlyDialogComponent, {
      data: this.formlyDialogData(),
      width: '60rem',
      panelClass: 'offer-form-dialog',
    });
    // sub
    dialogRef.componentInstance.saveData.subscribe((data) => {
      const offer = { ...data.formlyConfig.form.value };
      if (offer.costType) {
        offer[offer.costType] = offer.cost;
      }
      offer.id = this.offer.id;
      this.editOffer.emit({ offer: offer });
      dialogRef.close();
    });
  }

  acceptedToggleChange(checked: boolean) {
    this.acceptedToggle.emit({ id: this.offer.id, checked });
  }

  expandToggleEvent() {
    this.expandToggle.emit(this.offer.id);
  }

  setCostType() {
    if (this.offer.apr) {
      this.offer.costType = 'apr';
      this.offer.cost = this.offer.apr;
    } else if (this.offer.factor) {
      this.offer.costType = 'factor';
      this.offer.cost = this.offer.factor;
    } else if (this.offer.interestRate) {
      this.offer.costType = 'interest';
      this.offer.cost = this.offer.interestRate;
    }
  }

  copyUrl(url: string) {
    this.clipboard.copyFromContent(url);
  }

  formattedOfferPoints(offerPoints: string): number | void {
    try {
      if (offerPoints === '.') {
        return;
      }
      const offerPointsAsNumber = Number(offerPoints);
      return offerPointsAsNumber;
    } catch (e: unknown) {
      this.rollbar.error({ error: e });
      return;
    }
  }

  handlePublish() {
    const dialogRef = this.dialog.open(PublishOfferDialogComponent, {
      width: '580px',
      data: {
        offer: this.offer,
      },
    });

    dialogRef.componentInstance.publishConfirmed.pipe(take(1)).subscribe(() => {
      this.sendOfferToBorrowerPortal();
    });
  }

  handleRemoveFromPortal() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        showHeader: true,
        headerText: 'Remove offer from portal',
        confirmationPromptText:
          'Once removed the borrower will no longer be able to view this offer and it will revert back to a draft in pipeline.',
        showCancelButton: true,
        confirmButtonText: 'Remove',
      },
    });
    dialogRef.componentInstance.confirm.pipe(take(1)).subscribe(() => {
      this.removeFromPortal.emit(this.offer);
      dialogRef.close();
    });
  }

  /**
   * Send Offer data over to the Contract Request component for submission
   */
  requestContract() {
    this.offersService.emitOffer(this.offer);
  }

  private formlyDialogData(): FormlyDialogData {
    return {
      loading$: of(false),
      title: 'Edit Offer',
      formlyConfig: {
        model: { ...this.offer },
        form: new UntypedFormGroup({}),
        fields:
          this.offer.loanProductType !== 'factoring'
            ? this.offerFormFields.getOfferFields
            : this.offerFormFields.getFactoringFields,
        options: {
          formState: {
            mainModel: { ...this.offer },
          },
        },
      },
    };
  }
}
